import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { LINK_ENTITY_TYPE } from 'owa-attachment-link-common';
import { isHTMLAnchorElement, isHTMLElement } from 'owa-cross-window-types';
import { timestamp } from 'owa-datetime';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getEntitySelector } from 'roosterjs-editor-dom';
import {
    ChangeSource,
    EditorPlugin,
    EntityOperation,
    EntityOperationEvent,
    IEditor,
    PluginEvent,
    PluginEventType,
    PluginKeyboardEvent,
    PositionType,
} from 'roosterjs-editor-types';
import { cleanUpFluidInEditor } from './cleanUpFluidInEditor';
import { tryGetCodeDetails } from './tryGetCodeDetails';
import { fluidifyLink } from '../components/FluidLinkConverter';
import { COMPOSE_ONLY_FLUID_KEEP_BODY, FLUID_ENTITY_TYPE } from '../utils/constants';
import { FluidOwaSource, isDownLevelFluidLink } from 'owa-fluid-validations';
import { mountPastedFluidComponent } from '../utils/mountComponent/mountPastedFluidComponent';
import { tryGetFluidDriverForUrl } from '../utils/tryGetFluidDriverForUrl';
import { removeReactContent } from './removeReactContent';
import PluginNames from 'owa-editor-lazy-plugin/lib/utils/PluginNames';

export class FluidPlugin implements EditorPlugin {
    private editor: IEditor | null | undefined;
    private addRecipientFunc: (address: string) => void | undefined;
    readonly owaSource: FluidOwaSource;
    readonly viewStateId: string;

    constructor(
        public editorViewState: EditorViewState,
        owaSource: FluidOwaSource,
        addRecipientFunc: (address: string) => void | undefined,
        viewStateId: string
    ) {
        this.owaSource = owaSource;
        this.addRecipientFunc = addRecipientFunc;
        this.viewStateId = viewStateId;
    }

    getName() {
        return PluginNames.Fluid;
    }

    initialize(editor: IEditor) {
        this.editor = editor;
    }

    dispose() {
        this.editor = null;
    }

    onPluginEvent(e: PluginEvent) {
        switch (e.eventType) {
            case PluginEventType.EntityOperation:
                if (e.entity.type === FLUID_ENTITY_TYPE) {
                    this.onEntityOperation(e);
                }
                break;
            case PluginEventType.BeforePaste:
                // Used to measure E2E perf from when the content is pasted until the fluid component is rendered
                const scenarioStartTime = timestamp();

                e.fragment && e.fragment.normalize();
                const childrenNodes = e.fragment.childNodes;
                childrenNodes.forEach(node => {
                    const resolved = this.tryResolveNode(node, scenarioStartTime);
                    if (!resolved && node instanceof Element) {
                        node.querySelectorAll('a, span[fluid-url], div[fluid-url]').forEach(ele =>
                            this.tryResolveNode(ele, scenarioStartTime)
                        );
                    }
                });

                e.sanitizingOption.additionalAllowedCssClasses.push(COMPOSE_ONLY_FLUID_KEEP_BODY);
                break;

            case PluginEventType.EditorReady:
                this.editor!.queryElements('a', node => {
                    this.tryResolveNode(node, timestamp());
                });
                break;
            case PluginEventType.KeyDown:
                this.preventDiscardDraft(e);
                break;
            case PluginEventType.BeforeDispose:
                this.cleanUpFluidContent();
                break;
            case PluginEventType.ContentChanged:
                if (e.source === ChangeSource.SetContent) {
                    // VSO 111289: correctly handle undo
                    this.cleanUpFluidContent();
                }
                break;
        }
    }

    private onEntityOperation(event: EntityOperationEvent) {
        switch (event.operation) {
            case EntityOperation.RemoveFromStart:
            case EntityOperation.RemoveFromEnd:
            case EntityOperation.Overwrite:
                this.editor!.addUndoSnapshot(() => {
                    const { wrapper } = event.entity;
                    // We must also clean up the beautiful link Entity from inside the fluid entity.
                    const beautifulLinkSelector = getEntitySelector(LINK_ENTITY_TYPE);
                    const beautifulLinks = wrapper.querySelectorAll(beautifulLinkSelector);

                    if (event.rawEvent) {
                        event.rawEvent.preventDefault();
                        event.rawEvent.stopPropagation();
                    }

                    // Removes the beautiful link and the fluid entity from Reacts DOM tree
                    removeReactContent(wrapper);

                    // We also need to remove from the editors DOM tree.
                    if (beautifulLinks?.[0]) {
                        this.editor!.deleteNode(beautifulLinks[0]);
                    }
                    this.editor!.deleteNode(wrapper);
                });
                break;
            case EntityOperation.ReplaceTemporaryContent:
                cleanUpFluidInEditor(event.entity.wrapper);
                break;
            case EntityOperation.NewEntity:
                // VSO 111289: correctly handle undo will use this to track elements that need to
                // be unmounted.
                break;
        }
    }

    //Public only for testing mocks.
    public tryResolveNode = async (node: Node, scenarioStartTime: number) => {
        let href: string | null;

        // If it is an anchor element, use it's href.
        // Else, if it's an HTML element, use the fluid-url attribute if it exists.
        // Else, use the text content.
        if (isHTMLAnchorElement(node)) {
            href = node.href;
        } else if (isHTMLElement(node) && node.hasAttribute('fluid-url')) {
            href = node.getAttribute('fluid-url');
        } else {
            href = node.textContent;
        }
        if (href) {
            // Links which are part of the html blob for collab space support in down level clients
            // need to be prevented from hydrating.
            if (isDownLevelFluidLink(node, href)) {
                return true;
            }

            const driverInfo = tryGetFluidDriverForUrl(href);
            if (driverInfo) {
                mountPastedFluidComponent(
                    this.editor!,
                    node,
                    driverInfo,
                    href,
                    scenarioStartTime,
                    this.owaSource,
                    this.addRecipientFunc,
                    this.viewStateId
                );
                return true;
            }
            if (isFeatureEnabled('cmp-fluidStream')) {
                const codeDetails = await tryGetCodeDetails(href);
                if (codeDetails) {
                    fluidifyLink(
                        this.editorViewState,
                        node,
                        codeDetails,
                        this.owaSource,
                        this.editor!.getDocument(),
                        this.addRecipientFunc,
                        this.viewStateId
                    );
                }
                return true;
            }
        }
        return false;
    };

    private cleanUpFluidContent() {
        const selector = getEntitySelector(FLUID_ENTITY_TYPE);
        const fluidEntities = this.editor!.queryElements(selector);
        fluidEntities.forEach(entity => {
            removeReactContent(entity);
        });
    }

    // If focus is within a fluid component when escape is pressed we stop propagation on the escape event so that focus will
    // leave the component, but we won't close the draft.
    private preventDiscardDraft(event: PluginKeyboardEvent) {
        if (event.rawEvent.which === KeyCodes.escape) {
            const fluidContainer = this.editor!.getElementAtCursor(
                '[data-fluid-container]',
                event.rawEvent.target as Node
            );
            if (fluidContainer) {
                event.rawEvent.stopPropagation();
                this.editor!.focus();
                this.editor!.select(
                    fluidContainer.parentElement || fluidContainer,
                    PositionType.After
                );
            }
        }
    }
}
